import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Table,
  TableColumn,
  Badge,
  Button,
  PageHeader,
  Spinner,
  Input,
  Select,
  Modal,
} from '../../components';
import type { SelectOption } from '../../components';
import {
  MagnifyingGlassIcon,
  ChevronRightIcon,
  DownloadIcon,
} from '@radix-ui/react-icons';
import { auditService } from '../../services/auditService';
import type { AuditLog, AuditLogFilters, AuditLogStatus } from '../../types/auditLog';
import { formatApiError } from '../../utils/errorHandler';
import './AuditLogs.scss';

const MODULE_OPTIONS: SelectOption[] = [
  { value: '', label: 'All Modules' },
  { value: 'auth', label: 'Auth' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'users', label: 'Users' },
  { value: 'leads', label: 'Leads' },
  { value: 'kanban', label: 'Kanban' },
  { value: 'employees', label: 'Employees' },
  { value: 'plans', label: 'Plans' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'settings', label: 'Settings' },
  { value: 'notifications', label: 'Notifications' },
];

const STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'success', label: 'Success' },
  { value: 'failure', label: 'Failure' },
];

const ACTION_OPTIONS: SelectOption[] = [
  { value: '', label: 'All Actions' },
  { value: 'LOGOUT', label: 'Logout' },
  { value: 'PAGE_VIEW', label: 'Page View' },
  { value: 'LIST_VIEW', label: 'List View' },
  { value: 'PROFILE_VIEW', label: 'Profile View' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
  { value: 'BLOCK', label: 'Block' },
  { value: 'UNBLOCK', label: 'Unblock' },
  { value: 'EXPORT', label: 'Export' },
  { value: 'BULK_DELETE', label: 'Bulk Delete' },
  { value: 'BULK_EXPORT', label: 'Bulk Export' },
  { value: 'BULK_IMPORT', label: 'Bulk Import' },
  { value: 'STATUS_CHANGE', label: 'Status Change' },
  { value: 'COMMENT_ADD', label: 'Comment Added' },
  { value: 'ACTIVATE', label: 'Activate' },
  { value: 'DEACTIVATE', label: 'Deactivate' },
  { value: 'PLAN_ASSIGN', label: 'Plan Assigned' },
  { value: 'INVOICE_DOWNLOAD', label: 'Invoice Downloaded' },
];

const PAGE_SIZE_OPTIONS: SelectOption[] = [
  { value: '20', label: '20 / page' },
  { value: '50', label: '50 / page' },
  { value: '100', label: '100 / page' },
];

type BadgeVariant = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'default';

const ACTION_VARIANT: Record<string, BadgeVariant> = {
  LOGOUT: 'default',
  CREATE: 'primary',
  UPDATE: 'warning',
  DELETE: 'danger',
  BULK_DELETE: 'danger',
  BLOCK: 'danger',
  UNBLOCK: 'success',
  HIDE: 'warning',
  UNHIDE: 'success',
  ACTIVATE: 'success',
  DEACTIVATE: 'danger',
  EXPORT: 'info',
  BULK_EXPORT: 'info',
  BULK_IMPORT: 'info',
  PLAN_ASSIGN: 'primary',
  STATUS_CHANGE: 'warning',
  INVOICE_DOWNLOAD: 'info',
  PAGE_VIEW: 'default',
  LIST_VIEW: 'default',
  BOARD_VIEW: 'default',
  PROFILE_VIEW: 'default',
};

const MODULE_VARIANT: Record<string, BadgeVariant> = {
  auth: 'primary',
  dashboard: 'info',
  users: 'success',
  leads: 'warning',
  kanban: 'info',
  employees: 'danger',
  plans: 'primary',
  invoices: 'warning',
  settings: 'default',
  notifications: 'default',
};

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: true,
    });
  } catch {
    return ts;
  }
}

function actionLabel(action: string): string {
  return action.replace(/_/g, ' ');
}

interface LogDetailDrawerProps {
  log: AuditLog;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
}

const LogDetailDrawer: React.FC<LogDetailDrawerProps> = ({ log, isLoading, error, onClose }) => {
  const hasDiff = log.before || log.after;

  return (
    <Modal isOpen onClose={onClose} title="Log Detail" size="large">
      <div className="audit-detail">
        {isLoading && (
          <div className="audit-detail__loading">
            <Spinner size="small" />
            <span>Loading full log details...</span>
          </div>
        )}

        {error && (
          <div className="audit-detail__error-banner">{error}</div>
        )}

        <div className="audit-detail__grid">
          <div className="audit-detail__row"><span className="audit-detail__label">ID</span><code className="audit-detail__code">{log.auditLogId}</code></div>
          <div className="audit-detail__row"><span className="audit-detail__label">Timestamp</span><span>{formatTimestamp(log.timestamp)}</span></div>
          <div className="audit-detail__row"><span className="audit-detail__label">Session ID</span><code className="audit-detail__code">{log.sessionId}</code></div>
          <div className="audit-detail__row"><span className="audit-detail__label">Admin</span><span>{log.adminName} ({log.adminEmail})</span></div>
          <div className="audit-detail__row"><span className="audit-detail__label">Role</span><span>{log.adminRole}</span></div>
          <div className="audit-detail__row"><span className="audit-detail__label">Action</span><Badge variant={ACTION_VARIANT[log.action] ?? 'default'} size="small">{actionLabel(log.action)}</Badge></div>
          <div className="audit-detail__row"><span className="audit-detail__label">Module</span><Badge variant={MODULE_VARIANT[log.module] ?? 'default'} size="small">{log.module}</Badge></div>
          {log.subModule && <div className="audit-detail__row"><span className="audit-detail__label">Sub-module</span><span>{log.subModule}</span></div>}
          {log.targetType && <div className="audit-detail__row"><span className="audit-detail__label">Target type</span><span>{log.targetType}</span></div>}
          {log.targetId && <div className="audit-detail__row"><span className="audit-detail__label">Target ID</span><code className="audit-detail__code">{log.targetId}</code></div>}
          {log.targetName && <div className="audit-detail__row"><span className="audit-detail__label">Target name</span><span>{log.targetName}</span></div>}
          <div className="audit-detail__row"><span className="audit-detail__label">Status</span><Badge variant={log.status === 'success' ? 'success' : 'danger'} size="small">{log.status}</Badge></div>
          {log.errorMessage && <div className="audit-detail__row"><span className="audit-detail__label">Error</span><span className="audit-detail__error">{log.errorMessage}</span></div>}
          {log.url && <div className="audit-detail__row"><span className="audit-detail__label">URL</span><span className="audit-detail__url">{log.url}</span></div>}
          {log.userAgent && <div className="audit-detail__row"><span className="audit-detail__label">User Agent</span><span className="audit-detail__url">{log.userAgent}</span></div>}
          {log.ipAddress && <div className="audit-detail__row"><span className="audit-detail__label">IP Address</span><code className="audit-detail__code">{log.ipAddress}</code></div>}
        </div>

        {hasDiff && (
          <div className="audit-detail__diff">
            <p className="audit-detail__diff-title">Change Diff</p>
            <div className="audit-detail__diff-cols">
              {log.before && (
                <div className="audit-detail__diff-col audit-detail__diff-col--before">
                  <p className="audit-detail__diff-label">Before</p>
                  <pre>{JSON.stringify(log.before, null, 2)}</pre>
                </div>
              )}
              {log.after && (
                <div className="audit-detail__diff-col audit-detail__diff-col--after">
                  <p className="audit-detail__diff-label">After</p>
                  <pre>{JSON.stringify(log.after, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

const DEBOUNCE_MS = 400;

export const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState('');
  const [module, setModule] = useState('');
  const [action, setAction] = useState('');
  const [status, setStatus] = useState<AuditLogStatus | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const buildFilterParams = useCallback((): AuditLogFilters => ({
    ...(module ? { module } : {}),
    ...(action ? { action } : {}),
    ...(status ? { status } : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    ...(search ? { search } : {}),
  }), [module, action, status, startDate, endDate, search]);

  const buildParams = useCallback(
    (overrides: Partial<AuditLogFilters> = {}): AuditLogFilters => ({
      page: currentPage,
      limit: pageSize,
      ...buildFilterParams(),
      ...overrides,
    }),
    [currentPage, pageSize, buildFilterParams]
  );

  const fetchLogs = useCallback(
    async (params: AuditLogFilters) => {
      setIsLoading(true);
      setListError(null);
      try {
        const result = await auditService.list(params);
        setLogs(result.data);
        setTotal(result.metaData.total);
        setTotalPages(result.metaData.totalPages);
      } catch (err) {
        setLogs([]);
        setTotal(0);
        setTotalPages(1);
        setListError(formatApiError(err) || 'Failed to load audit logs.');
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchLogs(buildParams({ page: 1 }));
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, action, status, startDate, endDate, pageSize]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchLogs(buildParams({ page: 1, search: value }));
      setCurrentPage(1);
    }, DEBOUNCE_MS);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchLogs(buildParams({ page }));
  };

  const handleLogSelect = useCallback(async (log: AuditLog) => {
    setSelectedLog(log);
    setDetailError(null);
    setIsDetailLoading(true);

    try {
      const detail = await auditService.getById(String(log.auditLogId));
      setSelectedLog(detail);
    } catch (err) {
      setDetailError(formatApiError(err) || 'Failed to load audit log detail.');
    } finally {
      setIsDetailLoading(false);
    }
  }, []);

  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      await auditService.exportReport(buildFilterParams());
    } catch (err) {
      setExportError(formatApiError(err) || 'Failed to export audit logs.');
    } finally {
      setIsExporting(false);
    }
  };

  const hasActiveFilters =
    Boolean(search.trim()) ||
    Boolean(module) ||
    Boolean(action) ||
    Boolean(status) ||
    Boolean(startDate) ||
    Boolean(endDate);

  const handleClearFilters = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    setSearch('');
    setModule('');
    setAction('');
    setStatus('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    fetchLogs({ page: 1, limit: pageSize });
  }, [fetchLogs, pageSize]);

  const columns: TableColumn<AuditLog>[] = useMemo(() => [
    {
      key: 'timestamp',
      header: 'Timestamp',
      width: '175px',
      render: (log) => (
        <span className="audit-table__timestamp">{formatTimestamp(log.timestamp)}</span>
      ),
    },
    {
      key: 'admin',
      header: 'Admin',
      render: (log) => (
        <div className="audit-table__admin">
          <span className="audit-table__admin-name">{log.adminName}</span>
          <span className="audit-table__admin-role">{log.adminRole}</span>
        </div>
      ),
    },
    {
      key: 'module',
      header: 'Module',
      width: '140px',
      render: (log) => (
        <div className="audit-table__module">
          <Badge variant={MODULE_VARIANT[log.module] ?? 'default'} size="small">{log.module}</Badge>
          {log.subModule && (
            <span className="audit-table__submodule">{log.subModule}</span>
          )}
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      width: '160px',
      render: (log) => (
        <Badge variant={ACTION_VARIANT[log.action] ?? 'default'} size="small">
          {actionLabel(log.action)}
        </Badge>
      ),
    },
    {
      key: 'target',
      header: 'Target',
      render: (log) => (
        <span className="audit-table__target">
          {log.targetName || log.targetId || <span className="audit-table__na">—</span>}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '90px',
      align: 'center',
      render: (log) => (
        <Badge variant={log.status === 'success' ? 'success' : 'danger'} size="small">
          {log.status}
        </Badge>
      ),
    },
    {
      key: 'detail',
      header: '',
      width: '48px',
      align: 'center',
      render: (log) => (
        <button
          className="audit-table__expand-btn"
          onClick={(e) => { e.stopPropagation(); handleLogSelect(log); }}
          title="View detail"
        >
          <ChevronRightIcon />
        </button>
      ),
    },
  ], [handleLogSelect]);

  return (
    <div className="audit-logs">
      <PageHeader
        title="Audit Logs"
        subtitle={`${total.toLocaleString()} total events recorded`}
        actions={
          <Button variant="secondary" size="small" onClick={handleExport} disabled={isExporting}>
            <DownloadIcon style={{ marginRight: 6 }} />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        }
      />

      {exportError && (
        <div className="audit-logs__error">{exportError}</div>
      )}

      <div className="audit-logs__filters">
        <div className="audit-logs__filter-search">
          <div className="audit-logs__search-wrap">
            <MagnifyingGlassIcon className="audit-logs__search-icon" />
            <Input
              placeholder="Search by admin name, email, or target..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>
        <Select
          options={MODULE_OPTIONS}
          value={module}
          onChange={(v) => setModule(v)}
          placeholder="Module"
        />
        <Select
          options={ACTION_OPTIONS}
          value={action}
          onChange={(v) => setAction(v)}
          placeholder="Action"
        />
        <Select
          options={STATUS_OPTIONS}
          value={status}
          onChange={(v) => setStatus(v as AuditLogStatus | '')}
          placeholder="Status"
        />
        <div className="audit-logs__filter-dates">
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="From"
          />
          <span className="audit-logs__date-sep">—</span>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="To"
          />
        </div>
        <Select
          options={PAGE_SIZE_OPTIONS}
          value={String(pageSize)}
          onChange={(v) => setPageSize(Number(v))}
        />
        <button
          type="button"
          className="audit-logs__clear-filters"
          onClick={handleClearFilters}
          disabled={!hasActiveFilters}
        >
          Clear All
        </button>
      </div>

      {listError && (
        <div className="audit-logs__error">{listError}</div>
      )}

      <Table<AuditLog>
        columns={columns}
        data={logs}
        isLoading={isLoading}
        emptyMessage="No audit logs found for the selected filters."
        onRowClick={handleLogSelect}
        pagination={{
          currentPage,
          totalPages,
          pageSize,
          total,
          onPageChange: handlePageChange,
        }}
        className="audit-logs__table"
      />

      {selectedLog && (
        <LogDetailDrawer
          log={selectedLog}
          isLoading={isDetailLoading}
          error={detailError}
          onClose={() => {
            setSelectedLog(null);
            setDetailError(null);
            setIsDetailLoading(false);
          }}
        />
      )}
    </div>
  );
};
